/* eslint-disable react/no-access-state-in-setstate */
import Axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import EmployerDatabaseResultTable from '../../components/Employer/EmployerDatabaseResultTable/EmployerDatabaseResultTable';
import MaternityTipModal from '../../components/Employer/MaternityTipModal/MaternityTipModal';
import BasicDropdown from '../../components/Input/BasicDropdown/BasicDropdown';
import Button from '../../components/Input/Button/Button';
/* COMPONENTS */
import FormElementWithLabel from '../../components/Input/FormElementWithLabel/FormElementWithLabel';
import InputField from '../../components/Input/InputField/InputField';
import SinglePageLayout from '../../components/Layout/SinglePageLayout/SinglePageLayout';
import PageNav from '../../components/Management/Layout/PageNav/PageNav';
import HeaderSection from '../../components/Resources/HeaderSection/HeaderSection';
import ResourceList from '../../components/Resources/ResourceList/ResourceList';
import SearchContainer from '../../components/Resources/SearchContainer/SearchContainer';
import { SnowplowPageViewTracker } from '../../components/Analytics/Snowplow/SnowplowPageViewTracker';
import { throttle } from '../../utilities/helperFunctions';
/* CONSTANTS */
import {
    companyFieldLabelProps,
    industryFieldLabelProps,
    LEAVE_POLICY_TYPES,
    PAGE_LIMIT,
    SEARCH_API,
    TABLE_HEADERS,
    THROTTLE_INTERVAL,
} from './constants';
import { PAGE_PROPS } from '../defaultProps';

/* STYLESHEET */
import './MaternityLeaveResourceCenter.scss';

export default class MaternityLeaveResourceCenter extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            companyName: '',
            industry: null,
            sortBy: 'paidMaternity',
            order: 'desc',
            limit: 20,
            page: 1,
            searchResults: [],
            showTipModal: false,
            tip: {
                companyId: null,
                companyName: null,
            },
        };

        this.throttlePushUrl = throttle(
            this.pushUrlToWindow,
            THROTTLE_INTERVAL
        );
    }

    componentDidMount = () => {
        const queryValues = this.getUrlParams();
        this.setState(queryValues, this.search);
    };

    getUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);

        const companyName = urlParams.get('company') || '';
        const industry = urlParams.get('industry') || '';
        const page = parseInt(urlParams.get('page'), 10) || 1;
        const sortBy = urlParams.get('sortBy') || this.state.sortBy;
        const order = urlParams.get('order') || this.state.order;

        return { companyName, industry, page, sortBy, order };
    };

    setUrlParams = () => {
        const { companyName, industry, page, sortBy, order } = this.state;

        if (window.history.pushState) {
            const { protocol, host, pathname } = window.location;
            const companyQuery = companyName ? `company=${companyName}&` : '';
            const industryQuery = industry ? `industry=${industry}&` : '';
            const sortByQuery = sortBy ? `sortBy=${sortBy}&` : '';
            const orderQuery = order ? `order=${order}&` : '';
            const pageQuery = `page=${page}`;

            const url = `${protocol}//${host}${pathname}`;
            const queries = `${companyQuery}${industryQuery}${sortByQuery}${orderQuery}${pageQuery}`;

            const newUrl = `${url}?${queries}`;
            this.throttlePushUrl(newUrl);
        }
    };

    pushUrlToWindow = (newUrl) => {
        window.history.replaceState({}, '', newUrl);
    };

    flattenDataForTable = (data) => {
        const { company, leavePolicies } = data;

        const { companyName, industry, companyId } = company;
        const { aggregatedCompanyLeaveStats } = leavePolicies;
        const {
            paidMaternity,
            unpaidMaternity,
            paidPaternity,
            unpaidPaternity,
        } = aggregatedCompanyLeaveStats;

        return [
            companyId,
            companyName,
            industry,
            paidMaternity,
            unpaidMaternity,
            paidPaternity,
            unpaidPaternity,
        ];
    };

    getCancelToken = () => {
        if (typeof this._source !== typeof undefined) {
            this._source.cancel('Operation canceled due to new request.');
        }

        // save the new request for cancellation
        this._source = Axios.CancelToken.source();

        return {
            cancelToken: this._source.token,
        };
    };

    search = async () => {
        const {
            companyName,
            industry,
            sortBy,
            order,
            limit,
            page,
        } = this.state;

        this.setUrlParams();

        const cancelToken = this.getCancelToken();
        const payload = {
            filters: {
                companyName:
                    companyName && companyName.length > 0 ? companyName : null,
                industry,
                leavePolicyType: LEAVE_POLICY_TYPES,
            },
            sorting: {
                type: sortBy,
                order,
            },
            options: {
                leavePolicies: {
                    disabled: false,
                },
                numberOfComments: 0,
            },
            companyPagination: {
                limit,
                page,
            },
        };

        const { data } =
            (await Axios.post(SEARCH_API, payload, cancelToken)) || {};
        this.setState({ searchResults: data.map(this.flattenDataForTable) });
    };

    /** ************* FOR PAGINATION ************** */
    handlePrevious = () =>
        this.setState({ page: this.state.page - 1 }, this.search);

    handleNext = () =>
        this.setState({ page: this.state.page + 1 }, this.search);
    /** ************* FOR PAGINATION ************** */

    /** ************* FOR SEARCH ************** */
    handleSelect = (value) => {
        this.setState({ industry: value, page: 1 }, this.search);
    };

    handleChange = (value) => {
        this.setState({ companyName: value, page: 1 }, this.search);
    };

    handleFormReset = () => {
        this.setState(
            {
                companyName: '',
                industry: '',
            },
            this.search
        );
    };
    /** ************* FOR SEARCH ************** */

    /** ************* FOR TABLE CTA ************** */
    handleTipClick = (companyId, companyName) => {
        this.setState({
            showTipModal: true,
            tip: {
                companyId,
                companyName,
            },
        });
    };

    handleSort = (sortBy, order) =>
        this.setState({ sortBy, order }, this.search);

    handleModalClose = () => {
        this.setState({ showTipModal: false });
    };
    /** ************* FOR TABLE CTA ************** */

    renderSearchFields = () => {
        return (
            <SearchContainer onReset={this.handleFormReset}>
                <FormElementWithLabel fieldLabelProps={companyFieldLabelProps}>
                    <InputField
                        name="companyName"
                        placeholder="Enter a company"
                        inputValue={this.state.companyName}
                        onChange={this.handleChange}
                    />
                </FormElementWithLabel>

                <FormElementWithLabel fieldLabelProps={industryFieldLabelProps}>
                    <BasicDropdown
                        defaultLabel="Select industry"
                        options={this.props.searchBar.industryList}
                        onChange={this.handleSelect}
                        selectedValue={this.state.industry}
                        shouldDisableDefault
                    />
                </FormElementWithLabel>

                <Button
                    formAction="reset"
                    className="reset-button"
                    type="secondary-blue-gray"
                    value="Clear"
                />
            </SearchContainer>
        );
    };

    renderSearchResultsTable = () => {
        return (
            <div className="data-table">
                <EmployerDatabaseResultTable
                    tableHeaders={TABLE_HEADERS}
                    rowData={this.state.searchResults.slice(0, 20)}
                    onTipClick={this.handleTipClick}
                    onSort={this.handleSort}
                    sortType={this.state.sortBy}
                    sortOrder={this.state.order}
                />
            </div>
        );
    };

    renderPageNavigation = () => {
        const goPrev = this.state.page !== 1 ? this.handlePrevious : null;
        const goNext =
            this.state.searchResults.length > PAGE_LIMIT
                ? this.handleNext
                : null;
        const hasSearchResults = this.state.searchResults.length > 0;

        if (hasSearchResults) {
            return <PageNav goToPrevPage={goPrev} goToNextPage={goNext} />;
        }
    };

    render() {
        return (
            <div className="page-MaternityLeaveResourceCenter">
                <SnowplowPageViewTracker />
                <SinglePageLayout PAGE_PROPS={this.props.PAGE_PROPS}>
                    <SinglePageLayout.Header>
                        <HeaderSection
                            title="Parental Leave Resource Center"
                            subheading="We crowdsource parental leave information because so few companies share this data publicly."
                            description="Thankfully, many of you believe that transparency is a good thing. Below, we have compiled a list of the companies where we have collected data from our users. Where users report conflicting information, we show consensus numbers (if any) or the median, and the maximum and minimum numbers reported. To see the full range of data reported for any company, click on the company name. Please submit an anonymous tip to make this an even better, free resource!"
                        />
                    </SinglePageLayout.Header>

                    <SinglePageLayout.Content>
                        <main>
                            {this.renderSearchFields()}
                            {this.renderSearchResultsTable()}
                            {this.renderPageNavigation()}
                        </main>

                        <sidebar>
                            <ResourceList
                                resources={this.props.relatedResources}
                            />
                        </sidebar>

                        {this.state.showTipModal && (
                            <MaternityTipModal
                                companyId={this.state.tip.companyId}
                                companyName={this.state.tip.companyName}
                                showModal={this.state.showTipModal}
                                onClose={this.handleModalClose}
                            />
                        )}
                    </SinglePageLayout.Content>
                </SinglePageLayout>
            </div>
        );
    }
}

MaternityLeaveResourceCenter.propTypes = {
    /**
     * object of searchBar
     */
    PAGE_PROPS: PAGE_PROPS.isRequired,
    searchBar: PropTypes.shape({
        industryList: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.string,
            })
        ).isRequired,
    }).isRequired,
    /**
     * array of related resources with title, image, url
     */
    relatedResources: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            image: PropTypes.shape({
                src: PropTypes.string,
                alt: PropTypes.string,
            }).isRequired,
            url: PropTypes.string.isRequired,
        })
    ).isRequired,
};
